import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { AutoMessageDetailAppointmentEvent } from '@frontend/auto-message-detail-appointment-event';
import { useTranslation } from '@frontend/i18n';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { useAppointmentEventCardShallowStore } from '../../../stores/use-appointment-event-card-store';
import { ExtendedAppointment } from '../../../types';
import {
  OverlappingEventIndicator,
  OverlappingEventIndicatorVariantType,
} from '../../calendar-view/calendar-view-v3/overlapping-event-indicator';
import { EventCardComponentProps } from '../../calendar-view/providers';
import { prepareAppointmentInfo, shouldRenderStatusIconInline } from '../helpers';
import { MotionText } from '../motion-text';
import { AppointmentDetails } from './appointment-details';
import { NotesSection } from './notes-section';
import { PersonDetails } from './person-details';

interface Props {
  appointment: ExtendedAppointment;
  closeAppointmentDetailsModal?: () => void;
  eventId: string;
  locationName: string;
  referenceId?: string;
}

type AppointmentEventCardPreviewProps = {
  name: string;
  duration: string;
  renderStatusChip?: () => JSX.Element;
  renderInsuranceStatusChip?: () => JSX.Element;
  isNonAttendedV3Event?: boolean;
} & Pick<EventCardComponentProps, 'overlappingIndicatorVariant' | 'isYesterdayEvent'>;

export const AppointmentEventCardPreview = ({
  name,
  duration,
  renderStatusChip,
  renderInsuranceStatusChip,
  overlappingIndicatorVariant,
  isYesterdayEvent,
  isNonAttendedV3Event,
}: AppointmentEventCardPreviewProps) => {
  const overlappingIndicator = (variant: OverlappingEventIndicatorVariantType) => (
    <OverlappingEventIndicator
      variant={variant}
      isYesterdayEvent={isYesterdayEvent}
      visible={overlappingIndicatorVariant === variant}
    />
  );

  const shouldRenderInline = shouldRenderStatusIconInline(duration, !!renderStatusChip, !!renderInsuranceStatusChip);

  const statusIcons = () => {
    return (
      <div css={statusIconContainerStyle}>
        {renderStatusChip?.()}
        {renderInsuranceStatusChip?.()}
      </div>
    );
  };

  return (
    <div css={{ position: 'relative' }}>
      <div css={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        <section css={{ minWidth: 0, flex: 1 }}>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <MotionText size='medium' weight='bold' css={textStyle(isNonAttendedV3Event)}>
              {name}
            </MotionText>
            {overlappingIndicator('dot')}
          </div>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <MotionText size='small' color='light' css={textStyle(isNonAttendedV3Event)}>
              {duration}
            </MotionText>
            {overlappingIndicator('icon')}
          </div>
          {overlappingIndicator('full')}
        </section>
        {shouldRenderInline && statusIcons()}
      </div>
      {!shouldRenderInline && statusIcons()}
    </div>
  );
};

const statusIconContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
  marginTop: theme.spacing(0.25),
  flexShrink: 0,
  '> div:not(:first-child)': {
    marginLeft: theme.spacing(0.5),
  },
});

const textStyle = (strikeThrough?: boolean) =>
  css({
    textDecoration: strikeThrough ? 'line-through' : 'none',
    flex: 'unset',
  });

export const AppointmentEventCard = ({
  appointment,
  closeAppointmentDetailsModal,
  eventId,
  locationName,
  referenceId = '',
}: Props) => {
  const { t } = useTranslation('schedule');
  const { getLocationTimezoneAbbr } = useLocationTimezone();
  const appointmentInfo = useMemo(() => {
    const appointmentInfoToPrepare = appointment ? [appointment] : [];
    return prepareAppointmentInfo(appointmentInfoToPrepare ?? [], eventId, getLocationTimezoneAbbr);
  }, [appointment, eventId, getLocationTimezoneAbbr]);

  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const hasApptReminderFlag = getCustomizationFlagValue('apptreminders-automated');

  const { setAppointmentDetails, setIsIntegratedOffice, setCloseAppointmentDetailsModal } =
    useAppointmentEventCardShallowStore(
      'setAppointmentDetails',
      'setIsIntegratedOffice',
      'setCloseAppointmentDetailsModal'
    );

  const { isIntegratedOffice, isLoading: isLoadingIntegrationDetails } =
    DataSourcesQueries.useGetAccessibleIntegrations();

  useEffect(() => {
    if (appointment) {
      setAppointmentDetails(appointment);
    }
    setIsIntegratedOffice(isIntegratedOffice);
    !!closeAppointmentDetailsModal && setCloseAppointmentDetailsModal(closeAppointmentDetailsModal);
  }, [isIntegratedOffice, appointment]);

  useEffect(() => {
    return () => setAppointmentDetails(null);
  }, []);

  if (isLoadingIntegrationDetails) {
    return <AppointmentEventCardPreview name={t('Loading...')} duration={t('Loading...')} />;
  }

  const autoMessageRecordId = !!referenceId ? referenceId : appointmentInfo?.id ?? '';

  if (!appointmentInfo) {
    return null;
  }
  return (
    <>
      <PersonDetails
        locationId={appointmentInfo.locationId}
        personInfo={appointmentInfo.personInfo}
        appointmentId={appointmentInfo.appointmentDetails.referenceId}
        serviceableIds={appointmentInfo?.petInfo.serviceableIds || []}
        closeAppointmentDetailsModal={closeAppointmentDetailsModal}
      />
      <AppointmentDetails
        appointmentDetails={appointmentInfo.appointmentDetails}
        locationName={locationName}
        locationId={appointmentInfo.locationId}
        appointmentId={eventId}
        isIntegratedOffice={isIntegratedOffice}
      />
      {hasApptReminderFlag && (
        <AutoMessageDetailAppointmentEvent
          appointmentId={autoMessageRecordId}
          appointmentLocationId={appointmentInfo.locationId}
          closeAppointmentDetailsModal={closeAppointmentDetailsModal}
          person={appointmentInfo.personInfo}
        />
      )}
      {appointmentInfo.personInfo.householdId && <NotesSection householdId={appointmentInfo.personInfo.householdId} />}
    </>
  );
};
